// Hilfsfunktionen für die Statistik im neuen Design (StatistikBereich, StatistikSpielerDialog, VergleichBereich)
import {useEffect, useState} from "react";

// Grenzen wie in publicTournamentNeu.css: 600px Handy, 900px Tablet
export const BREITE_HANDY = 600;
export const BREITE_TABLET = 900;

/**
 * true bis zur angegebenen Breite (Standard Handy), aktualisiert sich beim Drehen bzw. bei Änderung der Fenstergröße.
 */
export function useSchmaleAnsicht(maxBreite = BREITE_HANDY) {
    const medienAbfrage = "(max-width: " + maxBreite + "px)";
    const [schmal, setSchmal] = useState(() => window.matchMedia(medienAbfrage).matches);
    useEffect(() => {
        const abfrage = window.matchMedia(medienAbfrage);
        setSchmal(abfrage.matches);
        const aendern = () => setSchmal(abfrage.matches);
        abfrage.addEventListener("change", aendern);
        return () => abfrage.removeEventListener("change", aendern);
    }, [medienAbfrage]);
    return schmal;
}

export const AUFNAHMEN_BEREICHE = [
    {key: "aufnahmen0", label: "0"},
    {key: "aufnahmen0Bis9", label: "0–9"},
    {key: "aufnahmen10Bis19", label: "10–19"},
    {key: "aufnahmen20Bis29", label: "20–29"},
    {key: "aufnahmen30Bis39", label: "30–39"},
    {key: "aufnahmen40Bis49", label: "40–49"},
    {key: "aufnahmen50Bis59", label: "50–59"},
    {key: "aufnahmen60Bis69", label: "60–69"},
    {key: "aufnahmen70Bis79", label: "70–79"},
    {key: "aufnahmen80Bis89", label: "80–89"},
    {key: "aufnahmen90Bis99", label: "90–99"},
    {key: "aufnahmen100Bis109", label: "100–109"},
    {key: "aufnahmen110Bis119", label: "110–119"},
    {key: "aufnahmen120Bis129", label: "120–129"},
    {key: "aufnahmen130Bis139", label: "130–139"},
    {key: "aufnahmen140Bis149", label: "140–149"},
    {key: "aufnahmen150Bis159", label: "150–159"},
    {key: "aufnahmen160Bis169", label: "160–169"},
    {key: "aufnahmen170Bis180", label: "170–180"},
];

// Farben der Diagramme, passend zu den Tokens in publicTournamentNeu.css (Canvas kennt keine CSS-Variablen)
export const FARBEN = {
    text: "#eaf3f6",
    textLeise: "#9bb7c2",
    linie: "rgba(255, 255, 255, 0.08)",
    gewonnen: "#3fd27a",
    verloren: "#e8413a",
    unentschieden: "#9bb7c2",
    spielerA: "#e8413a",
    spielerB: "#4fb3d9",
};

export function vorhanden(wert) {
    return wert !== null && wert !== undefined;
}

/**
 * Formatiert eine Zahl deutsch mit festen Nachkommastellen, fehlende Werte als "–".
 */
export function zahl(wert, stellen = 2) {
    if (!vorhanden(wert) || wert === "" || isNaN(Number(wert))) {
        return "–";
    }
    return Number(wert).toLocaleString("de-DE", {minimumFractionDigits: stellen, maximumFractionDigits: stellen});
}

/**
 * Anteil gewonnener Legs in Prozent, undefined wenn noch kein Leg gespielt wurde.
 */
export function legQuote(statistik) {
    if (!vorhanden(statistik)) {
        return undefined;
    }
    const gesamt = statistik.gewonneneLegs + statistik.verloreneLegs;
    if (gesamt === 0) {
        return undefined;
    }
    return statistik.gewonneneLegs / gesamt * 100;
}

export function anzahlAufnahmen(statistik) {
    return AUFNAHMEN_BEREICHE.reduce((summe, bereich) => summe + (statistik[bereich.key] || 0), 0);
}

/**
 * Teilnehmer mit ihrer Statistik, statistik ist undefined solange der Spieler noch nichts geworfen hat.
 */
export function getSpielerZeilen(participants, playerStatistiks) {
    return (participants || []).map(participant => ({
        id: participant.id,
        name: participant.name,
        statistik: (playerStatistiks || []).find(statistik => statistik.playerid === participant.id || statistik.idShort === participant.idShort),
    }));
}

/**
 * Highscores/Highfinishes zählen, z.B. [180, 140, 180] → [{wert: 180, anzahl: 2}, {wert: 140, anzahl: 1}].
 */
export function zaehleWerte(werte) {
    if (!Array.isArray(werte)) {
        return [];
    }
    const anzahl = new Map();
    for (const wert of werte) {
        anzahl.set(wert, (anzahl.get(wert) || 0) + 1);
    }
    return [...anzahl.entries()]
        .map(([wert, n]) => ({wert, anzahl: n}))
        .sort((a, b) => b.wert - a.wert);
}

/**
 * Gemeinsame Optionen der Diagramme auf dunklem Grund.
 */
export function getDiagrammOptionen(zusatz = {}) {
    return {
        maintainAspectRatio: false,
        animation: window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches ? false : undefined,
        plugins: {
            legend: {labels: {color: FARBEN.text}},
            ...zusatz.plugins,
        },
        scales: zusatz.scales,
        ...zusatz.weitere,
    };
}

export function getAchsen(yTitel) {
    return {
        x: {ticks: {color: FARBEN.textLeise}, grid: {color: FARBEN.linie}},
        y: {
            beginAtZero: true,
            ticks: {color: FARBEN.textLeise, precision: 0},
            grid: {color: FARBEN.linie},
            title: yTitel ? {display: true, text: yTitel, color: FARBEN.textLeise} : undefined,
        },
    };
}
